import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import {
    ArrowLeft,
    Package,
    SearchLg,
    Eye
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import { collaborationService } from '@/data/mock-collaborations';
import { accountService } from '@/data/mock-accounts';
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import type { Account } from '@/types/account';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';

export const ReceiverDetailPage = () => {
    const { t } = useTranslation(['pages:receivers', 'pages:containers', 'types', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const { id } = useParams<{ id: string }>();
    const [receiver, setReceiver] = useState<Account | null>(null);
    const [collaboration, setCollaboration] = useState<any>(null);
    const [containers, setContainers] = useState<Container[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels', { ns: 'pages:containers' }) },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    useEffect(() => {
        const loadData = async () => {
            if (!id) return;

            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage (sender)
            const accountId = localStorage.getItem('accountId') || '1';

            // Load receiver account
            const receiverData = accountService.getById(id);
            if (!receiverData) {
                setIsLoading(false);
                return;
            }
            setReceiver(receiverData);

            // Load collaboration
            const collaborations = collaborationService.getByShipperId(accountId);
            const receiverCollaboration = collaborations.find(c => c.receiverId === id);
            setCollaboration(receiverCollaboration);

            // Load containers from this sender to this receiver
            const allContainers = containerService.getAll();
            const receiverContainers = allContainers.filter(c =>
                c.shipperId === accountId && c.receiverId === id
            );
            setContainers(receiverContainers);

            setIsLoading(false);
        };
        loadData();
    }, [id]);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getCollaborationStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Pending': return 'warning';
            case 'Rejected': return 'error';
            case 'Suspended': return 'error';
            default: return 'gray';
        }
    };

    const getCollaborationStatusLabel = (status: string) => {
        switch (status) {
            case 'Active': return t('status.active', { ns: 'types' });
            case 'Pending': return t('status.pending', { ns: 'types' });
            case 'Rejected': return t('status.rejected', { ns: 'types' });
            case 'Suspended': return t('status.suspended', { ns: 'types' });
            default: return status;
        }
    };

    const filteredContainers = containers.filter(container => {
        const matchesSearch = !searchTerm ||
            container.containerNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            container.route.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
            container.route.to.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !statusFilter || container.status === statusFilter;
        const matchesRiskLevel = !riskLevelFilter || container.riskLevel === riskLevelFilter;
        const matchesDocumentStatus = !documentStatusFilter || container.documentStatus === documentStatusFilter;

        return matchesSearch && matchesStatus && matchesRiskLevel && matchesDocumentStatus;
    });

    // Calculate container statistics
    const containerStats = {
        total: containers.length,
        inTransit: containers.filter(c => c.status === 'In Transit').length,
        delivered: containers.filter(c => c.status === 'Delivered').length,
        delayed: containers.filter(c => c.status === 'Delayed').length,
        pendingDocuments: containers.filter(c => c.documentStatus === 'Pending').length,
        highRisk: containers.filter(c => c.riskLevel === 'High' || c.riskLevel === 'Critical').length,
    };

    // Calculate distribution data for charts
    const distributionData = {
        total: containers.length,
        riskData: [
            { label: t('riskLevel.low', { ns: 'types' }), value: containers.filter(c => c.riskLevel === 'Low').length, color: '#10B981' },
            { label: t('riskLevel.medium', { ns: 'types' }), value: containers.filter(c => c.riskLevel === 'Medium').length, color: '#F59E0B' },
            { label: t('riskLevel.high', { ns: 'types' }), value: containers.filter(c => c.riskLevel === 'High').length, color: '#EF4444' },
            { label: t('riskLevel.critical', { ns: 'types' }), value: containers.filter(c => c.riskLevel === 'Critical').length, color: '#DC2626' },
        ],
        statusData: [
            { label: t('containerStatus.inTransit', { ns: 'types' }), value: containers.filter(c => c.status === 'In Transit').length, color: '#3B82F6' },
            { label: t('containerStatus.delivered', { ns: 'types' }), value: containers.filter(c => c.status === 'Delivered').length, color: '#10B981' },
            { label: t('containerStatus.delayed', { ns: 'types' }), value: containers.filter(c => c.status === 'Delayed').length, color: '#EF4444' },
            { label: t('containerStatus.atPort', { ns: 'types' }), value: containers.filter(c => c.status === 'At Port').length, color: '#F59E0B' },
        ],
        documentData: [
            { label: t('documentStatus.complete', { ns: 'types' }), value: containers.filter(c => c.documentStatus === 'Completo').length, color: '#10B981' },
            { label: t('documentStatus.pending', { ns: 'types' }), value: containers.filter(c => c.documentStatus === 'Pending').length, color: '#F59E0B' },
            { label: t('documentStatus.error', { ns: 'types' }), value: containers.filter(c => c.documentStatus === 'Error').length, color: '#EF4444' },
        ],
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading', { ns: 'pages:receivers' }) || 'Caricamento...'}</p>
            </div>
        );
    }

    if (!receiver) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('detail.notFound.title', { ns: 'pages:receivers' }) || 'Receiver non trovato'}</h3>
                <p className="text-tertiary mb-4">{t('detail.notFound.message', { ns: 'pages:receivers' }) || 'Il receiver selezionato non esiste.'}</p>
                <Link
                    to="/shipper/receivers"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('detail.backToList', { ns: 'pages:receivers' }) || 'Torna alla lista'}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to="/shipper/receivers"
                        className="text-tertiary hover:text-primary"
                    >
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{receiver.legalName}</h1>
                        <p className="text-tertiary mt-1">
                            {receiver.country} • {receiver.email}
                        </p>
                    </div>
                </div>
                {collaboration && (
                    <Badge color={getCollaborationStatusColor(collaboration.status)} size="lg">
                        {getCollaborationStatusLabel(collaboration.status)}
                    </Badge>
                )}
            </div>

            {/* Receiver Info */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Anagrafica */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.companyInfo', { ns: 'pages:receivers' }) || 'Informazioni Azienda'}</h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.legalName', { ns: 'pages:receivers' }) || 'Ragione Sociale'}</div>
                                <div className="text-sm text-tertiary">{receiver.legalName}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.entityType', { ns: 'pages:receivers' }) || 'Tipo Entità'}</div>
                                <div className="text-sm text-tertiary">{receiver.entityType}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.taxId', { ns: 'pages:receivers' }) || 'Partita IVA'}</div>
                                <div className="text-sm text-tertiary">{receiver.taxId}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.country', { ns: 'pages:receivers' }) || 'Paese'}</div>
                                <div className="text-sm text-tertiary">{receiver.country}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-primary">{t('detail.info.fields.address', { ns: 'pages:receivers' }) || 'Indirizzo'}</div>
                            <div className="text-sm text-tertiary">
                                {receiver.address.street}, {receiver.address.city}, {receiver.address.state} {receiver.address.postalCode}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.email', { ns: 'pages:receivers' }) || 'Email'}</div>
                                <div className="text-sm text-tertiary">{receiver.email}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.phone', { ns: 'pages:receivers' }) || 'Telefono'}</div>
                                <div className="text-sm text-tertiary">{receiver.phone || 'N/A'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistiche Container */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.containerStats', { ns: 'pages:receivers' }) || 'Statistiche Container'}</h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-primary">{containerStats.total}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.total', { ns: 'pages:receivers' }) || 'Totale'}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-brand-600">{containerStats.inTransit}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.inTransit', { ns: 'pages:receivers' }) || 'In Transito'}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-success-600">{containerStats.delivered}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delivered', { ns: 'pages:receivers' }) || 'Consegnati'}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{containerStats.delayed}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delayed', { ns: 'pages:receivers' }) || 'In Ritardo'}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-warning-600">{containerStats.pendingDocuments}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.pendingDocuments', { ns: 'pages:receivers' }) || 'Documenti in Attesa'}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{containerStats.highRisk}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.highRisk', { ns: 'pages:receivers' }) || 'Alto Rischio'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Distribution Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <DistributionChart
                    title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Distribuzione Livelli di Rischio'}
                    data={distributionData.riskData}
                    total={distributionData.total}
                />
                <DistributionChart
                    title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Distribuzione Stato Container'}
                    data={distributionData.statusData}
                    total={distributionData.total}
                />
                <DistributionChart
                    title={t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Distribuzione Stato Documenti'}
                    data={distributionData.documentData}
                    total={distributionData.total}
                />
            </div>

            {/* Container List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-primary">{t('detail.containers.title', { ns: 'pages:receivers' }) || 'Container'}</h2>
                    <div className="text-sm text-tertiary">
                        {filteredContainers.length} {t('detail.containers.count', { ns: 'pages:receivers' }) || 'di'} {containers.length} {t('list.title', { ns: 'pages:containers' })}
                    </div>
                </div>

                {/* Filters */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Input
                            placeholder={t('detail.containers.searchPlaceholder', { ns: 'pages:receivers' }) || 'Cerca container...'}
                            value={searchTerm}
                            onChange={setSearchTerm}
                            icon={SearchLg}
                            size="md"
                        />

                        <Select
                            placeholder={t('detail.containers.statusPlaceholder', { ns: 'pages:receivers' }) || 'Filtra per stato'}
                            items={statusOptions}
                            selectedKey={statusFilter}
                            onSelectionChange={(key) => setStatusFilter(key as ContainerStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.riskPlaceholder', { ns: 'pages:receivers' }) || 'Filtra per rischio'}
                            items={riskLevelOptions}
                            selectedKey={riskLevelFilter}
                            onSelectionChange={(key) => setRiskLevelFilter(key as RiskLevel | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.documentsPlaceholder', { ns: 'pages:receivers' }) || 'Filtra per documenti'}
                            items={documentStatusOptions}
                            selectedKey={documentStatusFilter}
                            onSelectionChange={(key) => setDocumentStatusFilter(key as DocumentStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Containers Table */}
                <TableCard.Root>
                    <Table>
                        <Table.Header>
                            <Table.Head label={t('detail.containers.headers.containerId', { ns: 'pages:receivers' }) || 'ID Container'} isRowHeader />
                            <Table.Head label={t('detail.containers.headers.route', { ns: 'pages:receivers' }) || 'Percorso'} />
                            <Table.Head label={t('detail.containers.headers.progress', { ns: 'pages:receivers' }) || 'Progresso'} />
                            <Table.Head label={t('detail.containers.headers.eta', { ns: 'pages:receivers' }) || 'ETA'} />
                            <Table.Head label={t('detail.containers.headers.status', { ns: 'pages:receivers' }) || 'Stato'} />
                            <Table.Head label={t('detail.containers.headers.docStatus', { ns: 'pages:receivers' }) || 'Stato Documenti'} />
                            <Table.Head label={t('detail.containers.headers.riskLevel', { ns: 'pages:receivers' }) || 'Livello Rischio'} />
                            <Table.Head label={t('detail.containers.headers.value', { ns: 'pages:receivers' }) || 'Valore'} />
                            <Table.Head label={t('detail.containers.headers.actions', { ns: 'pages:receivers' }) || 'Azioni'} />
                        </Table.Header>
                        <Table.Body>
                            {filteredContainers.map((container) => (
                                <Table.Row key={container.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{container.containerNumber}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="text-sm text-tertiary">
                                            {container.route.from} → {container.route.to}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <ProgressBar
                                                value={container.progress}
                                                min={0}
                                                max={100}
                                            />
                                            <span className="text-sm text-tertiary">{container.progress}%</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {container.eta.toLocaleDateString('it-IT')}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getStatusColor(container.status)} size="sm">
                                            {translateContainerStatus(container.status)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                            {translateDocumentStatus(container.documentStatus)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                            {translateRiskLevel(container.riskLevel)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {container.value.currency} {container.value.amount.toLocaleString()}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link
                                            to={`/shipper/containers/${container.id}`}
                                            className="text-brand-secondary hover:text-brand-secondary_hover"
                                        >
                                            <Eye className="size-4" />
                                        </Link>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>

                {filteredContainers.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-tertiary mb-4">
                            <Package className="size-12 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-primary mb-2">{t('detail.containers.noContainers.title', { ns: 'pages:receivers' }) || 'Nessun container trovato'}</h3>
                            <p className="text-tertiary">
                                {searchTerm || statusFilter || riskLevelFilter || documentStatusFilter
                                    ? t('detail.containers.noContainers.message', { ns: 'pages:receivers' }) || 'Prova a modificare i filtri di ricerca'
                                    : t('detail.containers.noContainers.emptyMessage', { ns: 'pages:receivers' }) || 'Non ci sono container per questo receiver'
                                }
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

